'use client';

/**
 * PageTabBar Component
 *
 * Contextual tab navigation for pages with sub-sections.
 * Uses GlassTabs styling and syncs with URL for bookmarkable tabs.
 *
 * @see Issue #330 - Unified Navigation System
 */

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BadgeIcon from '@mui/icons-material/Badge';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChatIcon from '@mui/icons-material/Chat';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ContactsIcon from '@mui/icons-material/Contacts';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HistoryIcon from '@mui/icons-material/History';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import InfoIcon from '@mui/icons-material/Info';
import InsightsIcon from '@mui/icons-material/Insights';
import PaymentIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import NoteIcon from '@mui/icons-material/StickyNote2';
import StorageIcon from '@mui/icons-material/Storage';
import ActivityIcon from '@mui/icons-material/Timeline';
import { Box, Typography, useTheme } from '@mui/material';
import type { NavTab, VisibilityContext } from '@ops-matrix/shared-types';
import { checkVisibility } from '@ops-matrix/shared-types';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { type GlassTabItem, GlassTabs } from '../GlassTabs';

// Resolve a NavTab.icon string into a rendered MUI icon. Only a narrow set
// of icon names are supported — enough to cover every tabbed page's needs
// without pulling the full portal sidebar icon map into the tab bundle.
const TAB_ICON_MAP: Record<string, React.ReactElement> = {
  Dashboard: <DashboardIcon fontSize="small" />,
  Info: <InfoIcon fontSize="small" />,
  Description: <DescriptionIcon fontSize="small" />,
  Assignment: <AssignmentIcon fontSize="small" />,
  People: <PeopleIcon fontSize="small" />,
  Groups: <GroupsIcon fontSize="small" />,
  Checklist: <ChecklistIcon fontSize="small" />,
  Note: <NoteIcon fontSize="small" />,
  Chat: <ChatIcon fontSize="small" />,
  Gavel: <GavelIcon fontSize="small" />,
  Folder: <FolderIcon fontSize="small" />,
  Handshake: <HandshakeIcon fontSize="small" />,
  Activity: <ActivityIcon fontSize="small" />,
  History: <HistoryIcon fontSize="small" />,
  HomeWork: <HomeWorkIcon fontSize="small" />,
  Payment: <PaymentIcon fontSize="small" />,
  Insights: <InsightsIcon fontSize="small" />,
  Settings: <SettingsIcon fontSize="small" />,
  Security: <SecurityIcon fontSize="small" />,
  Badge: <BadgeIcon fontSize="small" />,
  AccountTree: <AccountTreeIcon fontSize="small" />,
  Campaign: <CampaignIcon fontSize="small" />,
  Storage: <StorageIcon fontSize="small" />,
  Contacts: <ContactsIcon fontSize="small" />,
  AttachMoney: <AttachMoneyIcon fontSize="small" />,
};

function resolveTabIcon(name: string | undefined): React.ReactElement | undefined {
  if (!name) return undefined;
  return TAB_ICON_MAP[name];
}

// ============================================================================
// Types
// ============================================================================

export interface PageTabBarProps {
  /** Section title to display */
  sectionTitle?: string;

  /** Available tabs */
  tabs: NavTab[];

  /** Currently active tab ID */
  activeTab: string;

  /** Callback when tab changes */
  onTabChange: (tabId: string, path: string) => void;

  /** Visibility context for filtering tabs */
  visibilityContext?: VisibilityContext;

  /** Tab size variant */
  size?: 'small' | 'medium' | 'large';

  /** Whether to show pill-shaped tabs */
  pill?: boolean;

  /** Glow color for active tab */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /** Whether to show glass effect */
  glass?: boolean;

  /** Sticky positioning */
  sticky?: boolean;

  /** Top offset for sticky positioning */
  stickyTop?: number | string;

  /**
   * Enable scrollable tabs when they overflow.
   * @default true — GlassTabs is now scrollable by default.
   * @deprecated GlassTabs handles overflow automatically. This prop is kept for backwards compat.
   */
  scrollable?: boolean;

  /**
   * Embedded mode — removes container background, border, and padding.
   * Use when PageTabBar is rendered inside another component (e.g., PageContentHeader)
   * that already provides visual boundaries.
   * @default false
   */
  embedded?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const PageTabBar: React.FC<PageTabBarProps> = ({
  sectionTitle,
  tabs,
  activeTab,
  onTabChange,
  visibilityContext = {},
  size = 'medium',
  pill = false,
  glowColor = 'primary',
  glass = true,
  sticky = true,
  stickyTop = 64, // Default AppBar height
  scrollable = true,
  embedded = false,
}) => {
  const theme = useTheme();
  const { t } = useI18n();

  // Filter tabs based on visibility
  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => !tab.visibility || checkVisibility(tab.visibility, visibilityContext));
  }, [tabs, visibilityContext]);

  // Convert NavTabs to GlassTabItems (with security props if lock config present)
  const glassTabItems: GlassTabItem[] = useMemo(() => {
    return visibleTabs.map((tab) => ({
      label: t(tab.label) || tab.label,
      value: tab.id,
      disabled: tab.disabled,
      icon: resolveTabIcon(tab.icon),
      // Pass lock config as security props to GlassTabs
      ...(tab.lock && {
        permission: tab.lock.permission,
        anyPermission: tab.lock.anyPermission,
        allPermissions: tab.lock.allPermissions,
        requiredTier: tab.lock.requiredTier,
        requiredLicense: tab.lock.requiredLicense,
        anyLicense: tab.lock.anyLicense,
      }),
    }));
  }, [visibleTabs, t]);

  // Handle tab change
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string | number) => {
      const tabId = String(newValue);
      const tab = visibleTabs.find((t) => t.id === tabId);
      if (tab) {
        onTabChange(tabId, tab.path);
      }
    },
    [visibleTabs, onTabChange],
  );

  // Don't render if no tabs or only one tab
  if (visibleTabs.length <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        ...(!embedded &&
          sticky && {
            position: 'sticky',
            top: stickyTop,
            zIndex: theme.zIndex.appBar - 1,
          }),
        ...(embedded
          ? { px: { xs: 1.5, sm: 2, md: 3 } }
          : {
              // Transparent wrapper — sits flat on whatever surface the page
              // already provides (page bg, card, panel). Painting an extra
              // background.paper here on top of an already-paper container
              // reads as a "card-in-card" double surface (issue: #3).
              bgcolor: 'transparent',
              // No heavy 2px wrapper border — GlassTabs already paints a
              // subtle baseline (`inset 0 -1px 0 alpha(divider, 0.6)`) under
              // every tab so the unselected tabs read against a continuous
              // low-contrast rule that matches typical card-bottom-border
              // weight, instead of two stacked separators of different
              // thickness.
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              mb: { xs: 2, sm: 2.5, md: 3 },
            }),
        overflowX: 'auto',
        overflowY: 'visible',
        // When rendered inside a flex-column parent with fixed height (as
        // in the partner/entity detail layouts), the tab bar gets shrunk
        // to <30px and becomes a sliver. Pin its height to its content so
        // it keeps ~40-60px of breathing room regardless of siblings.
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2, md: 3 },
          maxWidth: '100%',
        }}
      >
        {/* Section title */}
        {sectionTitle && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              minWidth: 'fit-content',
              display: { xs: 'none', md: 'block' },
              flexShrink: 0,
            }}
          >
            {t(sectionTitle) || sectionTitle}
          </Typography>
        )}

        {/* Tabs */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <GlassTabs
            tabs={glassTabItems}
            value={activeTab}
            onChange={handleChange}
            size={size}
            pill={pill}
            color={glowColor}
            glass={glass}
            {...(scrollable === false && { variant: 'standard' as const, scrollButtons: false as const })}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PageTabBar;
